import React, { useState } from "react";
import "@/App.css";
import axios from "axios";
import { markSessionCompleted } from "./lib/memberIdentity";

import Welcome from "./components/Welcome";
import SafetyScreener from "./components/SafetyScreener";
import SymptomCheckIn from "./components/SymptomCheckIn";
import ModeSelection from "./components/ModeSelection";
import SessionModeA from "./components/SessionModeA";
import SessionModeB from "./components/SessionModeB";
import GroundingExit from "./components/GroundingExit";
import SessionSummary from "./components/SessionSummary";
import Blocked from "./components/Blocked";
import Referred from "./components/Referred";
import TopNav from "./components/TopNav";
import AdminPanel from "./components/AdminPanel";
import MemberLoginModal from "./components/MemberLoginModal";
import MemberDashboard from "./components/MemberDashboard";
import EntitlementModal from "./components/EntitlementModal";
import SiteFooter from "./components/SiteFooter";
import { PrivacyPage, TermsPage } from "./components/LegalPages";
import { PaymentSuccessPage, PaymentCancelPage } from "./components/PaymentPages";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DemoProvider, useDemo } from "./components/DemoContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_LABELS = ["Screener", "Pre", "Mode", "Session", "Post", "Ground", "Summary"];

// Module 15 §15.4 — engine-only eligibility thresholds. Member never sees
// these numbers or the threshold itself; we just route the disposition.
const PHQ4_REFERRAL_THRESHOLD = 6;
const GAD7_REFERRAL_THRESHOLD = 10;

const initialState = () => ({
  patientName: null,
  patientEmail: null,
  initialActivation: null,
  screener: null,
  preSymptoms: null,
  postSymptoms: null,
  modeChoice: null,
  sessionMetrics: null,
  grounding: null,
  disposition: null,
  result: null,
});

function AppInner() {
  const [phase, setPhase] = useState("welcome");
  const [s, setS] = useState(initialState());
  const demo = useDemo();
  const { isMember, openLogin } = useAuth();
  const [entitlementGate, setEntitlementGate] = useState(null);

  const restart = () => {
    setS(initialState());
    setPhase("welcome");
    demo.stop();
  };

  // Demo banner STOP → return the member to Welcome instead of stranding
  // them mid-flow (DemoBanner dispatches "demo-exit").
  React.useEffect(() => {
    const onExit = () => restart();
    window.addEventListener("demo-exit", onExit);
    return () => window.removeEventListener("demo-exit", onExit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Welcome now passes { name, email, activation } (all optional). We accept
  // either shape so legacy demo entry points stay working.
  const applyIdentity = (identity) => {
    if (!identity) return;
    const name = typeof identity === "object" ? identity.name : null;
    const email =
      typeof identity === "object" ? identity.email : identity;
    const activation =
      typeof identity === "object" && identity.activation !== undefined
        ? identity.activation
        : null;
    setS((p) => ({
      ...p,
      ...(name ? { patientName: name } : {}),
      ...(email ? { patientEmail: email } : {}),
      ...(activation !== null && activation !== undefined
        ? { initialActivation: activation }
        : {}),
    }));
  };

  const startDemo = (identity = null) => {
    applyIdentity(identity);
    demo.start();
    demo.setStep("Safety screener");
    setPhase("screener");
  };

  const startSession = (identity = null) => {
    // Require member sign-in + an available code session before starting.
    const proceed = () => {
      applyIdentity(identity);
      setPhase("screener");
    };
    const checkEntitlementThen = async () => {
      try {
        const { data } = await axios.get(`${API}/me/entitlement`);
        if (data.can_start) proceed();
        else setEntitlementGate({ ...data, proceed });
      } catch (err) {
        console.error("Entitlement check failed", err);
        proceed(); // fail-open in the client; the server still enforces on save
      }
    };
    if (!isMember) {
      openLogin(checkEntitlementThen);
      return;
    }
    checkEntitlementThen();
  };

  const stepIndex =
    {
      screener: 0,
      "pre-symptoms": 1,
      "mode-selection": 2,
      "session-a": 3,
      "session-b": 3,
      "post-symptoms": 4,
      grounding: 5,
      summary: 6,
    }[phase] ?? 0;

  const saveSession = async (groundingPayload, partialMetrics = null) => {
    const isPartial = !!partialMetrics;
    if (!isPartial) markSessionCompleted();
    const m = partialMetrics || s.sessionMetrics || {};
    const payload = {
      mode: (isPartial ? m.mode : s.modeChoice.mode),
      pre_suds: s.preSymptoms.suds,
      // Post-symptoms are unavailable for a partial (stopped-early) session.
      post_suds: isPartial ? null : s.postSymptoms.suds,
      pre_phq4: s.preSymptoms.phq4,
      post_phq4: isPartial ? null : s.postSymptoms.phq4,
      pre_gad7: s.preSymptoms.gad7,
      post_gad7: isPartial ? null : s.postSymptoms.gad7,
      tap_count: m.tap_count ?? 0,
      tap_rate: m.tap_rate ?? 0,
      left_taps: m.left_taps ?? 0,
      right_taps: m.right_taps ?? 0,
      alternation_pct: m.alternation_pct ?? 0,
      consistency_pct: m.consistency_pct ?? 0,
      cadence_hz: m.cadence_hz ?? null,
      pacer_pattern: m.pacer_pattern ?? null,
      gaze_enabled: m.gaze_enabled ?? false,
      gaze_calibrated: m.gaze_calibrated ?? false,
      gaze_on_dot_pct: m.gaze_on_dot_pct ?? null,
      gaze_tracking_lag_ms: m.gaze_tracking_lag_ms ?? null,
      gaze_stability_index: m.gaze_stability_index ?? null,
      gaze_dropout_count: m.gaze_dropout_count ?? null,
      gaze_sample_count: m.gaze_sample_count ?? null,
      duration_seconds: m.duration_seconds ?? 0,
      engagement_score: m.engagement_score ?? 0,
      started_at: m.started_at ?? null,
      ended_at: m.ended_at ?? null,
      techniques: s.modeChoice?.techniques || [],
      grounding_completed: !!groundingPayload?.grounding_completed,
      partial: isPartial,
      disposition: s.disposition || "proceed",
      notes:
        (isPartial ? "[PARTIAL · stopped early] " : "") +
        (s.postSymptoms?.reflection ? `[REFLECTION] ${s.postSymptoms.reflection} · ` : "") +
        (groundingPayload?.grounding_notes || ""),
    };

    // Demo playthroughs never touch the backend (they run without sign-in and
    // must not create real records). Show a local-only summary.
    if (demo.active) {
      setS((prev) => ({
        ...prev,
        sessionMetrics: isPartial ? partialMetrics : prev.sessionMetrics,
        grounding: groundingPayload,
        result: {
          session: {
            ...payload,
            id: "demo",
            timestamp: new Date().toISOString(),
            escalation_flag: false,
            recovery_score: isPartial ? null : 74,
            recovery_basis: "self-report",
          },
          notion: { enabled: false, success: false, page_id: null, error: null },
        },
      }));
      setPhase("summary");
      demo.setStep("Session summary");
      setTimeout(() => demo.stop(), 200);
      return;
    }

    try {
      const { data } = await axios.post(`${API}/sessions`, payload);
      setS((prev) => ({
        ...prev,
        sessionMetrics: isPartial ? partialMetrics : prev.sessionMetrics,
        grounding: groundingPayload,
        result: data,
      }));
      setPhase("summary");
      demo.setStep("Session summary");
      setTimeout(() => demo.stop(), 200);
    } catch (e) {
      setS((prev) => ({
        ...prev,
        sessionMetrics: isPartial ? partialMetrics : prev.sessionMetrics,
        grounding: groundingPayload,
        result: {
          session: {
            ...payload,
            id: "local",
            timestamp: new Date().toISOString(),
            escalation_flag: false,
          },
          notion: { enabled: false, success: false, page_id: null, error: String(e) },
        },
      }));
      setPhase("summary");
      demo.setStep("Session summary");
      setTimeout(() => demo.stop(), 200);
    }
  };

  // Called by SessionModeA/B's STOP button — they pass a metrics snapshot.
  // Skip post-symptoms + grounding entirely; persist as a partial session and
  // jump straight to the Session Summary.
  const abortSession = (partialMetrics) => {
    saveSession(null, partialMetrics);
  };

  // Render the phase-specific screen. We wrap it in a fragment with the
  // sitewide TopNav above — except on the live session screens, where the
  // STOP button + pulsing pacer need the full visual budget (focus mode).
  const hideTopNav = phase === "session-a" || phase === "session-b";
  const screen = (() => {
    switch (phase) {
    case "welcome":
      return <Welcome onStart={startSession} onDemo={startDemo} />;
    case "screener":
      return (
        <SafetyScreener
          stepIndex={0}
          steps={STEP_LABELS}
          onPass={(scr) => {
            setS((p) => ({ ...p, screener: scr }));
            demo.setStep("Pre-session symptom check-in");
            setPhase("pre-symptoms");
          }}
          onBlocked={(scr) => {
            setS((p) => ({ ...p, screener: scr }));
            setPhase("blocked");
          }}
        />
      );
    case "pre-symptoms":
      return (
        <SymptomCheckIn
          phase="pre"
          stepIndex={1}
          steps={STEP_LABELS}
          initialSuds={s.initialActivation}
          onComplete={(data) => {
            // Module 15 §15.4 safety gate (engine-only): PHQ-4 ≥ 6 or
            // GAD-7 ≥ 10 routes to the warm "Referred" exit. Scores and
            // thresholds are never shown to the member. Demo mode skips
            // the gate so the auto-playthrough can complete a session.
            const overThreshold =
              !demo.active &&
              ((data.phq4 ?? 0) >= PHQ4_REFERRAL_THRESHOLD ||
                (data.gad7 ?? 0) >= GAD7_REFERRAL_THRESHOLD);
            if (overThreshold) {
              setS((p) => ({
                ...p,
                preSymptoms: data,
                disposition: "referred_screen_threshold",
              }));
              setPhase("referred");
              return;
            }
            setS((p) => ({
              ...p,
              preSymptoms: data,
              disposition: "proceed",
            }));
            demo.setStep("Choosing Mode A · Tap-Based");
            setPhase("mode-selection");
          }}
          onStop={restart}
        />
      );
    case "mode-selection":
      return (
        <ModeSelection
          stepIndex={2}
          steps={STEP_LABELS}
          onSelect={(c) => {
            setS((p) => ({ ...p, modeChoice: c }));
            demo.setStep(`Mode ${c.mode} session — bilateral stimulation`);
            setPhase(c.mode === "A" ? "session-a" : "session-b");
          }}
        />
      );
    case "session-a":
      return (
        <SessionModeA
          defaultTechniques={s.modeChoice?.techniques || []}
          preSuds={s.preSymptoms?.suds}
          onComplete={(m) => {
            setS((p) => ({ ...p, sessionMetrics: m }));
            demo.setStep("Post-session symptom check-in");
            setPhase("post-symptoms");
          }}
          onStop={(partialMetrics) => abortSession(partialMetrics)}
        />
      );
    case "session-b":
      return (
        <SessionModeB
          preSuds={s.preSymptoms?.suds}
          onComplete={(m) => {
            setS((p) => ({ ...p, sessionMetrics: m }));
            demo.setStep("Post-session symptom check-in");
            setPhase("post-symptoms");
          }}
          onStop={(partialMetrics) => abortSession(partialMetrics)}
        />
      );
    case "post-symptoms":
      return (
        <SymptomCheckIn
          phase="post"
          stepIndex={4}
          steps={STEP_LABELS}
          onComplete={(data) => {
            setS((p) => ({
              ...p,
              postSymptoms: data,
              sessionMetrics: p.sessionMetrics || {
                mode: p.modeChoice?.mode || "A",
                duration_seconds: 0,
                engagement_score: 0,
              },
            }));
            demo.setStep("5-4-3-2-1 grounding exit");
            setPhase("grounding");
          }}
          onStop={restart}
        />
      );
    case "grounding":
      return (
        <GroundingExit
          stepIndex={5}
          steps={STEP_LABELS}
          onDone={(g) => saveSession(g)}
        />
      );
    case "summary":
      return (
        <SessionSummary
          stepIndex={6}
          steps={STEP_LABELS}
          result={s.result}
          onRestart={restart}
        />
      );
    case "blocked":
      return <Blocked onRestart={restart} />;
    case "referred":
      return <Referred onRestart={restart} />;
    default:
      return <Welcome onStart={startSession} onDemo={startDemo} />;
    }
  })();

  return (
    <>
      {!hideTopNav && (
        <a href="#main" className="skip-link" data-testid="skip-to-content-link">
          Skip to content
        </a>
      )}
      {!hideTopNav && <TopNav />}
      {screen}
      <MemberLoginModal />
      <EntitlementModal
        gate={entitlementGate}
        onClose={() => setEntitlementGate(null)}
        onProceed={() => {
          const p = entitlementGate?.proceed;
          setEntitlementGate(null);
          if (typeof p === "function") p();
        }}
      />
    </>
  );
}

function SimpleShell({ children, withFooter = false }) {
  return (
    <>
      <a href="#main" className="skip-link" data-testid="skip-to-content-link">
        Skip to content
      </a>
      <TopNav />
      {children}
      {withFooter && <SiteFooter />}
      <MemberLoginModal />
    </>
  );
}

function App() {
  const path =
    typeof window !== "undefined" ? window.location.pathname : "/";
  const isAdminRoute = path.startsWith("/admin");
  const isDashboardRoute = path.startsWith("/dashboard");
  const isPrivacyRoute = path.startsWith("/privacy");
  const isTermsRoute = path.startsWith("/terms");

  let routed = null;
  if (isAdminRoute) routed = <AdminPanel />;
  else if (isDashboardRoute) routed = <SimpleShell withFooter><MemberDashboard /></SimpleShell>;
  else if (isPrivacyRoute) routed = <SimpleShell withFooter><PrivacyPage /></SimpleShell>;
  else if (isTermsRoute) routed = <SimpleShell withFooter><TermsPage /></SimpleShell>;
  else if (path.startsWith("/payment/success")) routed = <SimpleShell><PaymentSuccessPage /></SimpleShell>;
  else if (path.startsWith("/payment/cancel")) routed = <SimpleShell><PaymentCancelPage /></SimpleShell>;

  return (
    <AuthProvider>
      {routed ? (
        routed
      ) : (
        <DemoProvider>
          <AppInner />
        </DemoProvider>
      )}
    </AuthProvider>
  );
}

export default App;
